import { SelectOptionHide } from '@App/stores/search/models/select-option-hide'
import { EXACT_MATCH_SUFFIX } from '@App/helpers/map-criteria-to-object'

/** Every query-string key a set of criteria options can occupy: each option's key and its exact-match twin. */
export const getCriteriaParamNames = (options: Array<SelectOptionHide<any>>): string[] =>
    options.flatMap((option) => [option.value, `${option.value}${EXACT_MATCH_SUFFIX}`])

const isBlank = (value: unknown) => value === undefined || value === null || value === ''

/**
 * Reads the known criteria out of a query string, in the same shape `mapCriteriaToObject` produces
 * (so `combinedTitlesExact=x` stays `combinedTitlesExact`). A repeated key becomes an array.
 * Undefined when none of the options' keys are present.
 */
export const parseCriteriaFromSearch = (
    search: string,
    options: Array<SelectOptionHide<any>>
): Record<string, unknown> | undefined => {
    const params = new URLSearchParams(search)
    const result: Record<string, unknown> = {}

    getCriteriaParamNames(options).forEach((name) => {
        const values = params.getAll(name).filter((value) => !isBlank(value))
        if (values.length > 0) {
            result[name] = values.length === 1 ? values[0] : values
        }
    })

    return Object.keys(result).length > 0 ? result : undefined
}

/**
 * Returns `currentSearch` with the known criteria keys replaced by `criteria` (blank values omitted),
 * leaving unrelated params — filters, a tab hash's siblings — untouched. Includes the leading "?"
 * or is empty.
 */
export const serializeCriteriaToSearch = (
    currentSearch: string,
    criteria: object | undefined,
    options: Array<SelectOptionHide<any>>
): string => {
    const params = new URLSearchParams(currentSearch)
    const names = getCriteriaParamNames(options)
    names.forEach((name) => params.delete(name))

    names.forEach((name) => {
        const value = (criteria as Record<string, unknown> | undefined)?.[name]
        const entries = Array.isArray(value) ? value : [value]
        entries.filter((entry) => !isBlank(entry)).forEach((entry) => params.append(name, String(entry)))
    })

    const search = params.toString()

    return search ? `?${search}` : ''
}
